import { useState } from 'react';

// Confirmation on Quote Ready / Consignment for a quote that holds pairable
// (speaker-type) items. Speakers get quoted with qty counting individual units
// instead of pairs, or a genuine pair is never flagged — either corrupts the
// offer. This lists ONLY the pairable items (isPairable, pairable.js) and makes
// the operator confirm is_pair + qty before the offer commits.
//
// One confirm for the whole quote, not a per-item acknowledgement — repeated
// per-item acks train click-through (same reasoning as ConfirmOfferDialog).
export default function PairConfirmDialog({ items, busy, onCancel, onConfirm }) {
    // Edit state seeded from each item, keyed by the stable quote_item_id.
    const [edits, setEdits] = useState(() =>
        Object.fromEntries(items.map(it => [it.itemId, { is_pair: it.is_pair, qty: String(it.qty) }])));
    const [error, setError] = useState(null);

    const setPair = (id, is_pair) => setEdits(e => ({ ...e, [id]: { ...e[id], is_pair } }));
    const setQty = (id, qty) => setEdits(e => ({ ...e, [id]: { ...e[id], qty } }));

    function confirm() {
        const changed = [];
        for (const it of items) {
            const cur = edits[it.itemId];
            const quantity = Number(cur.qty);
            if (!Number.isInteger(quantity) || quantity < 1) {
                setError('Quantity must be a whole number of 1 or more.');
                return;
            }
            // Only rows that actually moved travel to the parent's patchItem.
            if (cur.is_pair !== it.is_pair || quantity !== it.qty) {
                changed.push({ itemId: it.itemId, is_pair: cur.is_pair, quantity });
            }
        }
        onConfirm(changed);
    }

    return (
        <div className="modal-overlay">
            <div className="modal-content qa-pair-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Confirm pairs &amp; quantities</h2>
                    <button className="modal-close" onClick={onCancel}>&times;</button>
                </div>
                <div className="modal-body-scroll">
                    <div className="qa-pair-explain">
                        <strong>This quote has speaker-type items.</strong> A pair counts as one:
                        if an item is flagged as a <em>pair</em> and the customer has two speakers,
                        the quantity is <strong>1</strong>, not 2. Check each item below is flagged
                        correctly and its quantity is right before the offer goes out.
                    </div>
                    {items.map(it => {
                        const cur = edits[it.itemId];
                        return (
                            <div key={it.itemId} className="qa-pair-row">
                                <div className="qa-pair-row-top">
                                    <div className="qa-pair-name">{it.name}</div>
                                    <div className="qa-pair-controls">
                                        <label className="qa-pair-toggle">
                                            <input type="checkbox" checked={cur.is_pair}
                                                onChange={e => setPair(it.itemId, e.target.checked)} />
                                            Pair
                                        </label>
                                        <label className="qa-pair-qty">
                                            <span>Qty</span>
                                            <input type="number" min="1" step="1" className="qa-pair-qty-input"
                                                value={cur.qty} onChange={e => setQty(it.itemId, e.target.value)} />
                                        </label>
                                    </div>
                                </div>
                                <div className={`qa-pair-hint${cur.is_pair ? ' is-pair' : ''}`}>
                                    {cur.is_pair
                                        ? 'Flagged as a pair, so Qty is the number of pairs, not individual units.'
                                        : 'Not marked as a pair. Tick Pair if these are sold as one.'}
                                </div>
                            </div>
                        );
                    })}
                    {error && <div className="qa-pair-error">{error}</div>}
                </div>
                <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
                    <button className="btn btn-quote-ready" onClick={confirm} disabled={busy}>{busy ? '…' : 'Confirm & send'}</button>
                </div>
            </div>
        </div>
    );
}
